import axiosInstance from '../../config/axios.config';

/**
 * LinkedInService - LinkedIn OAuth and Profile Sync Service
 * Handles LinkedIn integration for profile import
 */
const LinkedInService = {
    /**
     * Get LinkedIn OAuth authorization URL
     * @returns {Promise} Object with authUrl
     */
    getConnectUrl: async () => {
        const response = await axiosInstance.get('/leadflow/linkedin/connect');
        return response.data;
    },

    /**
     * Handle LinkedIn OAuth callback
     * This is typically handled by the backend redirect
     * @param {string} code - Authorization code
     * @param {string} state - State parameter
     * @returns {Promise} OAuth callback result
     */
    handleCallback: async (code, state) => {
        const response = await axiosInstance.get('/leadflow/linkedin/callback', {
            params: { code, state }
        });
        return response.data;
    },

    /**
     * Trigger manual LinkedIn profile sync
     * Re-fetches data from LinkedIn and updates profile
     * @returns {Promise} Sync result with imported data summary
     */
    syncProfile: async () => {
        const response = await axiosInstance.post('/leadflow/linkedin/sync');
        return response.data;
    },

    /**
     * Get LinkedIn connection status
     * @returns {Promise} Object with connection status
     * - is_connected: boolean
     * - last_synced_at: timestamp
     * - token_expires_at: timestamp
     * - sync_enabled: boolean
     * - data_source: 'manual'|'linkedin'|'hybrid'
     */
    getStatus: async () => {
        const response = await axiosInstance.get('/leadflow/linkedin/status');
        return response.data;
    },

    /**
     * Disconnect LinkedIn account
     * Revokes OAuth token and updates data_source
     * @param {boolean} clearData - Whether to clear LinkedIn-imported data (optional)
     * @returns {Promise} Disconnection confirmation
     */
    disconnect: async (clearData = false) => {
        const response = await axiosInstance.post('/leadflow/linkedin/disconnect', {
            clear_data: clearData
        });
        return response.data;
    },

    /**
     * Preview what will be imported from LinkedIn
     * Shows comparison with existing data before syncing
     * @returns {Promise} Preview data
     */
    getPreview: async () => {
        const response = await axiosInstance.get('/leadflow/linkedin/preview');
        return response.data;
    },

    /**
     * Open LinkedIn OAuth popup window
     * @param {string} authUrl - LinkedIn authorization URL
     * @param {Function} onSuccess - Callback on successful auth
     * @param {Function} onError - Callback on error
     */
    openOAuthPopup: (authUrl, onSuccess, onError) => {
        const width = 600;
        const height = 700;
        const left = window.screen.width / 2 - width / 2;
        const top = window.screen.height / 2 - height / 2;

        const popup = window.open(
            authUrl,
            'LinkedIn OAuth',
            `width=${width},height=${height},left=${left},top=${top},toolbar=0,location=0,menubar=0`
        );

        // ONE teardown, called from every exit path (#218).
        //
        // Teardown used to be copy-pasted into the postMessage and popup-blocked
        // branches and simply forgotten in the poll-detected close — which is
        // the ordinary path, taken every time a user cancels the LinkedIn
        // dialog. Each cancel leaked a global `message` handler holding a
        // closure over popup/onSuccess/onError, and nothing ever released them.
        //
        // `settled` is what makes this safe to call more than once. Without it,
        // a postMessage that lands while getStatus() is already in flight fires
        // the caller's callback a second time — and on the success path that
        // means syncProfile() runs twice.
        let settled = false;

        const cleanup = () => {
            clearInterval(pollTimer);
            window.removeEventListener('message', messageHandler);
        };

        const settle = (fn, arg) => {
            if (settled) return;
            settled = true;
            cleanup();
            fn(arg);
        };

        // Poll for popup closure or message
        const pollTimer = setInterval(() => {
            // `popup` is null when the browser blocked it; the guard below clears
            // this timer synchronously, but read defensively so a blocked popup
            // can never throw inside the interval.
            if (!popup || popup.closed) {
                // Stop polling immediately: getStatus() is async, and without
                // this the timer fires again every 500ms while it is in flight.
                clearInterval(pollTimer);

                // Check if sync was successful by polling status
                LinkedInService.getStatus()
                    .then((status) => {
                        if (status.data && status.data.is_connected) {
                            settle(onSuccess, status.data);
                        } else {
                            settle(onError, new Error('LinkedIn connection was not completed'));
                        }
                    })
                    .catch((error) => settle(onError, error));
            }
        }, 500);

        // Listen for postMessage from popup (if backend sends it)
        const messageHandler = (event) => {
            if (event.data && event.data.type === 'LINKEDIN_OAUTH_SUCCESS') {
                if (popup && !popup.closed) {
                    popup.close();
                }
                settle(onSuccess, event.data.payload);
            } else if (event.data && event.data.type === 'LINKEDIN_OAUTH_ERROR') {
                if (popup && !popup.closed) {
                    popup.close();
                }
                settle(onError, new Error(event.data.error || 'LinkedIn OAuth failed'));
            }
        };

        window.addEventListener('message', messageHandler);

        // Cleanup if popup is blocked
        if (!popup || popup.closed) {
            settle(onError, new Error('Popup was blocked. Please allow popups for this site.'));
        }

        // Disposer for the caller's unmount path — navigating away while the
        // popup is open is an exit path too, and the caller is the only one who
        // knows it happened. Callers that ignore the return value are unaffected.
        return () => {
            settled = true;
            cleanup();
        };
    }
};

export default LinkedInService;
